import './ProjectListItemListView.scss';
import { ProjectAddress } from '@belimo-retrofit-portal/logic';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { AssetImage } from 'src/modules/common/components/AssetImage';
import { Button } from 'src/modules/common/components/Button';
import { FormattedBEP } from 'src/modules/common/components/FormattedBEP';
import { FormattedValue } from 'src/modules/common/components/FormattedValue';
import { OverflowMenu } from 'src/modules/common/components/OverflowMenu';
import { ProjectReadOnlyBadge } from 'src/modules/common/components/ProjectReadOnlyBadge';
import { ProjectStatusTag } from 'src/modules/common/components/ProjectStatusTag';
import { ProjectTitle } from 'src/modules/common/components/ProjectTitle';
import { CopyIcon } from 'src/modules/common/icons/CopyIcon';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { DownloadIcon } from 'src/modules/common/icons/DownloadIcon';
import { FolderIcon } from 'src/modules/common/icons/FolderIcon';
import { MoreIcon } from 'src/modules/common/icons/MoreIcon';
import { PDFIcon } from 'src/modules/common/icons/PDFIcon';
import { ShareIcon } from 'src/modules/common/icons/ShareIcon';
import { ShareOff } from 'src/modules/common/icons/ShareOff';
import { Project } from 'src/modules/common/types/Project';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { HelperText01 } from 'src/modules/common/typography/HelperText01';
import { ProjectListColumn, ProjectListColumnKey } from 'src/modules/project-list/types/ProjectListColumn';
import { ProjectListItem } from 'src/modules/project-list/types/ProjectListItem';
import { ProjectListItemMembers } from 'src/modules/project-list/views/ProjectListItemMembers';
import { ProjectListRating } from 'src/modules/project-list/views/ProjectListRating';
import { SharedUser } from 'src/modules/sharing-projects/types/SharedUser';
import { assertNever } from 'src/utils/assert';

type Props = {
  readonly item: ProjectListItem;
  readonly columns: ReadonlyArray<ProjectListColumn>;
  readonly downloading: boolean;
  readonly unfollowing: boolean;
  readonly clonning: boolean;
  readonly exporting: boolean;
  readonly scrollable: boolean;
  readonly onRemove: (project: Project) => void;
  readonly onDownload: (project: Project) => void;
  readonly onUnfollow: (project: Project) => void;
  readonly onShare: (project: Project) => void;
  readonly onClone: (project: Project) => void;
  readonly onExport: (project: Project) => void;
};

export const ProjectListItemListView = ({
  item,
  columns,
  downloading,
  unfollowing,
  clonning,
  exporting,
  scrollable,
  onRemove,
  onDownload,
  onUnfollow,
  onShare,
  onClone,
  onExport,
}: Props): React.ReactElement => {
  const intl = useIntl();

  const { project, rating, metrics, members } = item;
  const shared = project.isShared;

  const [menuOpen, setMenuOpen] = useState(false);

  const handleMenuOpen = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setMenuOpen(true);
  }, []);

  const handleRemove = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    onRemove(project);
  }, [project, onRemove]);

  const handleDownload = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    onDownload(project);
  }, [project, onDownload]);

  const handleUnfollow = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    onUnfollow(project);
  }, [project, onUnfollow]);

  const handleOwnershipClick = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    onShare(project);
  }, [project, onShare]);

  const handleClone = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    onClone(project);
  }, [project, onClone]);

  const handleExport = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    onExport(project);
  }, [project, onExport]);

  const buildingType = intl.formatMessage({ id: `common/buildingType/${project.data.building.type}` });

  return (
    <Link to={`/project/${encodeURIComponent(project.id)}`} className="bp-project-list-item-list-view">
      <div className="bp-project-list-item-list-view__column bp-project-list-item-list-view__column--image">
        {project.data.cover && (
          <AssetImage
            alt={project.data.title}
            asset={project.data.cover}
            size="small"
            className="bp-project-list-item-list-view__image"
          />
        )}
        {!project.data.cover && (
          <div className="bp-project-list-item-list-view__icon">
            <FolderIcon/>
          </div>
        )}

        {project.readOnly && (<ProjectReadOnlyBadge fixed={true}/>)}
      </div>

      <div className={clsx({
        'bp-project-list-item-list-view__column': true,
        'bp-project-list-item-list-view__column--title': true,
        'bp-project-list-item-list-view__column--title-overflow': scrollable,
      })}
      >
        <HelperText01 className="bp-project-list-item-list-view__no-wrap">
          {formatProjectDetailsSubTitle(buildingType, project.data.building.address)}
        </HelperText01>

        <BodyMedium01 className="bp-project-list-item-list-view__title">
          <ProjectTitle title={project.data.title}/>
        </BodyMedium01>
      </div>

      {columns.filter((column) => column.enabled).map((column) => {
        if (column.key === ProjectListColumnKey.MEMBERS) {
          return (
            <div key={column.key} className="bp-project-list-item-list-view__column">
              <ProjectListItemMembers owner={project.owner} members={members.length}/>
            </div>
          );
        }
        if (column.key === ProjectListColumnKey.STATUS) {
          return (
            <div key={column.key} className="bp-project-list-item-list-view__column">
              <BodyMedium01>
                <ProjectStatusTag projectStatus={project.data.status}/>
              </BodyMedium01>
            </div>
          );
        }
        if (column.key === ProjectListColumnKey.UPDATED) {
          return (
            <div key={column.key} className="bp-project-list-item-list-view__column">
              <BodyMedium01>
                <FormattedDate value={project.updatedAt}/>
              </BodyMedium01>
            </div>
          );
        }
        if (column.key === ProjectListColumnKey.ROI) {
          return (
            <div key={column.key} className="bp-project-list-item-list-view__column">
              <BodyMedium01>
                <FormattedValue
                  value={metrics.roiExclCO2?.mul(100) ?? null}
                  precision={1}
                />
              </BodyMedium01>
            </div>
          );
        }
        if (column.key === ProjectListColumnKey.ROI_2) {
          return (
            <div key={column.key} className="bp-project-list-item-list-view__column">
              <BodyMedium01>
                <FormattedValue
                  value={metrics.roiExclCO2_2?.mul(100) ?? null}
                  precision={1}
                />
              </BodyMedium01>
            </div>
          );
        }
        if (column.key === ProjectListColumnKey.ROI_5) {
          return (
            <div key={column.key} className="bp-project-list-item-list-view__column">
              <BodyMedium01>
                <FormattedValue
                  value={metrics.roiExclCO2_5?.mul(100) ?? null}
                  precision={1}
                />
              </BodyMedium01>
            </div>
          );
        }
        if (column.key === ProjectListColumnKey.BEP) {
          return (
            <div key={column.key} className="bp-project-list-item-list-view__column">
              <BodyMedium01>
                <FormattedBEP value={metrics.bepExclCO2}/>
              </BodyMedium01>
            </div>
          );
        }

        return assertNever('Invalid column', { column });
      })}

      <div
        className={clsx({
          'bp-project-list-item-list-view__column': true,
          'bp-project-list-item-list-view__column--rating': true,
          'bp-project-list-item-list-view__column--rating-overflow': scrollable,
        })}
      >
        <ProjectListRating rating={rating}/>
      </div>

      <div className="bp-project-list-item-list-view__column bp-project-list-item-list-view__column--menu">
        <OverflowMenu
          id={`project-list-item-${project.id}-menu`}
          buttonTooltip={intl.formatMessage({ id: 'projectList/items' })}
          opened={menuOpen}
          setOpened={setMenuOpen}
          label={(
            <Button
              className={clsx({
                'bp-project-list-item-list-view__overflow-button': true,
                'bp-project-list-item-list-view__overflow-button--opened': menuOpen,
              })}
              onClick={handleMenuOpen}
              kind="ghost"
              iconOnly={true}
              size="lg"
              reversed={true}
            >
              <div className="bp-project-list-item-list-view__overflow-button-icon">
                <MoreIcon/>
              </div>
            </Button>
          )}
        >
          <Button
            className="bp-project-list-item-list-view__button"
            kind="ghost"
            onClick={handleDownload}
            loading={downloading}
            icon={(
              <div className="bp-project-list-item-list-view__button-icon">
                <PDFIcon/>
              </div>
            )}
            reversed={true}
          >
            <FormattedMessage id="projectList/item/button/download"/>
          </Button>

          <Button
            className="bp-project-list-item-list-view__button"
            kind="ghost"
            onClick={handleExport}
            loading={exporting}
            icon={(
              <div className="bp-project-list-item-list-view__button-icon">
                <DownloadIcon/>
              </div>
            )}
            reversed={true}
          >
            <FormattedMessage id="projectList/item/button/export"/>
          </Button>

          <Button
            className="bp-project-list-item-list-view__button"
            kind="ghost"
            onClick={handleClone}
            loading={clonning}
            icon={(
              <div className="bp-project-list-item-list-view__button-icon">
                <CopyIcon/>
              </div>
            )}
            reversed={true}
          >
            <FormattedMessage id="projectList/item/button/clone"/>
          </Button>

          <Button
            className="bp-project-list-item-list-view__button"
            kind="ghost"
            onClick={handleOwnershipClick}
            icon={(
              <div className="bp-project-list-item-list-view__button-icon">
                <ShareIcon/>
              </div>
            )}
            reversed={true}
          >
            <FormattedMessage id="projectList/item/button/share"/>
          </Button>

          {shared ? (
            <Button
              className="bp-project-list-item-list-view__button"
              kind="ghost"
              onClick={handleUnfollow}
              loading={unfollowing}
              icon={(
                <div className="bp-project-list-item-list-view__button-icon">
                  <ShareOff/>
                </div>
              )}
              reversed={true}
            >
              <FormattedMessage id="projectList/item/button/unfollow"/>
            </Button>
          ) : (
            <Button
              className="bp-project-list-item-list-view__button bp-project-list-item-list-view__button--delete"
              kind="ghost"
              onClick={handleRemove}
              icon={(
                <div className="bp-project-list-item-list-view__button-icon">
                  <DeleteIcon/>
                </div>
              )}
              reversed={true}
            >
              <FormattedMessage id="projectList/item/button/delete"/>
            </Button>
          )}
        </OverflowMenu>
      </div>
    </Link>
  );
};

function formatProjectDetailsSubTitle(buildingType: string, address: ProjectAddress): string {
  return [buildingType, address.street.trim(), `${address.city} ${address.zip}`.trim()].filter(Boolean).join(', ');
}
